import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, tap, of, map } from "rxjs";
import { environment } from "../../../../environments/environment";
import { CriarRecadastramentoResponseDto } from "../../colaborador/models/Criar.Recadastramento.Response.Dto";
import { FinalizarRecadastramentoResponseDto } from "../../colaborador/models/Finalizar.Recadastramento.Response.Dto";
import { FinalizarRecadastramentoRequestDto } from "../../colaborador/models/Finalizar.Recadastramento.Request.Dto";
import { IniciarRecadastramentoRequestDto } from "../../colaborador/models/Iniciar.Recadastramento.Request.Dto";
import { IniciarRecadastramentoResponseDto } from "../../colaborador/models/Iniciar.Recadastramento.Response.Dto";

@Injectable({ providedIn: 'root' })
export class RecadastroService {
  constructor(private http: HttpClient) { }

  consultarRecadastroPorIntervalo(usuarioId: string, dataInicio: string, dataFim: string): Observable<CriarRecadastramentoResponseDto[]> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Ajustar datas para incluir o dia todo
    const dataInicioFormatada = `${dataInicio}T00:00:00Z`;
    const dataFimFormatada = `${dataFim}T23:59:59Z`;

    const params = new HttpParams()
      .set('dataInicio', dataInicioFormatada)
      .set('dataFim', dataFimFormatada);

    const url = `${environment.cedaeapp}/recadastro/usuario/${usuarioId}/obterintervalo`;

    return this.http.get<any>(url, { headers, params }).pipe(
      tap(response => console.log('✅ Recadastros recebidos (dados brutos):', response)),
      map(response => {
        // Check if response is directly an array or needs extraction
        if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.recadastros)) {
          return response.recadastros;
        } else if (response && typeof response === 'object') {
          // Try to find an array property in the response
          const arrayProps = Object.keys(response).filter(key => Array.isArray(response[key]));
          if (arrayProps.length > 0) {
            return response[arrayProps[0]];
          }
        }
        // If we can't determine the structure, return empty array
        console.warn('❓ Estrutura de resposta inesperada, retornando array vazio', response);
        return [];
      }),
      tap(parsedArray => console.log('✅ Array final de recadastros:', parsedArray)),
      catchError(err => {
        if (err.status === 404) {
          return of([]);
        }
        console.error('❌ Erro ao consultar recadastros por intervalo:', err);
        throw err;
      })
    );
  }

  adicionarRecadastro(recadastroId: string, usuarioId: string, formData?: any): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    let requestData = new FormData();

    // Always include the UserId
    requestData.append('UsuarioId', usuarioId);

    // Try to get original data to preserve admin fields

    // Process FormData object
    if (formData instanceof FormData) {
      // Get FormData entries
      const formEntries = Array.from(formData.entries());

      for (const [key, value] of formEntries) {
        // Skip null values
        if (value === null || value === undefined) continue;

        // Skip photos
        if (key.includes('Fotos') || key.toLowerCase().includes('foto')) continue;

        // Ensure field capitalization is consistent
        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);

        requestData.append(formattedKey, value as string | Blob);
      }
    }
    // Process regular object 
    else if (formData && typeof formData === 'object') {
      // ...existing code for processing regular objects...
    }

    const url = `${environment.cedaeapp}/recadastro/atualizar/${recadastroId}`;

    return this.http.put<any>(url, requestData, { headers }).pipe(
      tap(response => console.log('✅ Cadastro atualizado com sucesso:', response)),
      catchError(err => {
        console.error('❌ Erro ao atualizar cadastro:', err);
        throw err;
      })
    );
  }

  iniciarRecadastramento(recadastramento: IniciarRecadastramentoRequestDto): Observable<IniciarRecadastramentoResponseDto> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    if (!recadastramento.cadastroId) {
      console.error('❌ Erro: cadastroId é obrigatório para iniciar recadastramento');
      throw new Error('O ID do cadastro é obrigatório para iniciar o recadastramento');
    }

    const formData = new FormData();
    formData.append('UsuarioId', recadastramento.usuarioId);
    formData.append('LatitudeInicio', recadastramento.latitudeInicio);
    formData.append('LongitudeInicio', recadastramento.longitudeInicio);

    const url = `${environment.cedaeapp}/recadastro/iniciar/${recadastramento.cadastroId}`;

    return this.http.post<IniciarRecadastramentoResponseDto>(url, formData, { headers }).pipe(
      tap(response => {
        console.log('✅ Recadastramento iniciado com sucesso:', response);
        // Assuming IniciarRecadastramentoResponseDto has recadastroId and dataHoraRecadastramento
        localStorage.setItem('recadastramentoAtivo', JSON.stringify({
          cadastroId: recadastramento.cadastroId,
          recadastroId: response.recadastroId, // This correctly saves the recadastroId from the response
          dataHoraInicio: response.dataHoraRecadastramento || new Date().toISOString(), // Align with sample response field name
          usuarioId: response.usuarioId || recadastramento.usuarioId, // Prefer usuarioId from response if available
          latitudeInicio: response.latitudeInicio || recadastramento.latitudeInicio, // Prefer from response
          longitudeInicio: response.longitudeInicio || recadastramento.longitudeInicio // Prefer from response
        }));
      }),
      catchError(err => {
        console.error('❌ Erro ao iniciar recadastramento:', err);
        throw err;
      })
    );
  }

  finalizarRecadastramento(recadastramento: FinalizarRecadastramentoRequestDto): Observable<FinalizarRecadastramentoResponseDto> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();

    // Obter recadastroId do localStorage
    const recadastramentoAtivo = localStorage.getItem('recadastramentoAtivo');
    const dadosRecadastramento = recadastramentoAtivo ? JSON.parse(recadastramentoAtivo) : null;

    // Tentar obter o recadastroId do localStorage
    const recadastroIdDoStorage = dadosRecadastramento?.recadastroId;

    // Verificar se temos um recadastroId válido
    if (!recadastroIdDoStorage) {
      console.error('❌ Erro: Não foi encontrado um recadastroId válido para finalização');
      // Fallback to the recadastroId from the DTO if storage is missing, though ideally storage should be reliable.
      if (!recadastramento.recadastroId) {
        throw new Error('Não foi possível encontrar o ID do recadastramento para finalização nem no localStorage nem no DTO.');
      }
      console.warn('⚠️ Usando recadastroId do DTO para finalizar, pois não foi encontrado no localStorage.');
      // If using recadastroId from DTO, ensure it's the one from `iniciarRecadastramento` response.
      // The caller (EdicaoConsumidorComponent) sets `finalizarRequest.recadastroId = recadastroIdDoStorage`,
      // so `recadastramento.recadastroId` should be the same as `recadastroIdDoStorage` if storage was found.
    }

    const idParaFinalizar = recadastroIdDoStorage || recadastramento.recadastroId;

    if (!idParaFinalizar) {
      throw new Error('ID do Recadastramento para finalização é indefinido.');
    }

    // The RecadastroId is part of the URL path.
    // It's generally not needed in the FormData body if it's a path parameter.
    // formData.append('RecadastroId', idParaFinalizar); // Removed this line

    formData.append('LatitudeFim', recadastramento.latitudeFim);
    formData.append('LongitudeFim', recadastramento.longitudeFim);

    // Nova URL com recadastroId no path
    const url = `${environment.cedaeapp}/recadastro/finalizar/${idParaFinalizar}`;

    return this.http.put<FinalizarRecadastramentoResponseDto>(url, formData, { headers }).pipe(
      tap(response => {
        console.log('✅ Recadastramento finalizado com sucesso:', response);
        localStorage.removeItem('recadastramentoAtivo');
        console.log('🧹 Dados do recadastramento removidos do localStorage');
      }),
      catchError(err => {
        console.error('❌ Erro ao finalizar recadastramento:', err);
        throw err;
      })
    );
  }

  uploadAssinaturaVistoriante(cadastroId: string, file: File): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();
    formData.append('file', file);

    const url = `${environment.cedaeapp}/assinaturas/vistoriante/${cadastroId}/upload`;

    return this.http.post<any>(url, formData, { headers }).pipe(
      tap(() => console.log('✅ Assinatura do vistoriador enviada com sucesso.')),
      catchError(err => {
        console.error('❌ Erro ao enviar assinatura do vistoriador:', err);
        throw err;
      })
    );
  }

  uploadAssinaturaAcompanhante(cadastroId: string, file: File): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();
    formData.append('file', file);

    const url = `${environment.cedaeapp}/assinaturas/acompanhante/${cadastroId}/upload`;

    return this.http.post<any>(url, formData, { headers }).pipe(
      tap(() => console.log('✅ Assinatura do acompanhante enviada com sucesso.')),
      catchError(err => {
        console.error('❌ Erro ao enviar assinatura do acompanhante:', err);
        throw err;
      })
    );
  }

  
  /**
   * Atribui ou desatribui um recadastro ao usuário logado (ou contexto atual).
   * PATCH /cedae/recadastro/{recadastroId}/atribuir
   * @param recadastroId UUID do recadastro
   * @param atribuida true para atribuir, false para desatribuir
   * @returns Observable<any>
   */
  atribuirRecadastro(recadastroId: string, atribuida: boolean = true): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json-patch+json'
    });
    const url = `${environment.cedaeapp}/recadastro/${recadastroId}/atribuir`;
    // O body é true (atribuir) ou false (desatribuir)
    return this.http.patch<any>(url, atribuida, { headers }).pipe(
      tap(response => console.log(`✅ Recadastro ${atribuida ? 'atribuído' : 'desatribuído'} com sucesso:`, response)),
      catchError(err => {
        console.error(`❌ Erro ao ${atribuida ? 'atribuir' : 'desatribuir'} recadastro:`, err);
        throw err;
      })
    );
  }
}
